import { useCallback } from 'react';
import { useStaffProfile, useStudy } from 'src/helpers/hooks/useStudy';
import { IFormDto, IFormResultVersionDto, IPatientListDto } from 'src/services/api/api-client';
import { createReport, generateReportFileName, triggerDownloadFileDialog } from '../report-helpers';
import { normalizeFileName } from 'src/helpers/string-helper';
import { RecordsReportTemplate } from './recordsReport.layout';
import {
  FormListType,
  getRecordResultListItemVms,
} from 'src/components/formEditor/controls/ConstantBlocks/RecordResultList/useGetFormList';
import { getFormResults } from 'src/services/api/api-client/FormsClient';
import { FilledRecord } from './recordsReport';

export const useGenerateRecordReport = () => {
  const { profile } = useStaffProfile();
  const study = useStudy();

  return useCallback(
    async (
      patient: IPatientListDto,
      stepName: string | undefined,
      formResultVersion: IFormResultVersionDto,
      formConfig: IFormDto,
    ) => {
      if (!study?.studyNumber || !patient?.uniqueId) return;

      const fileName = generateReportFileName({
        studyNumber: study.studyNumber,
        uniqueId: patient.uniqueId,
        formTypes: [formConfig.type],
      });

      let mandatoryForms: FormListType[] = [];
      let multipleForms: FormListType[] = [];

      const formConfigIdsForNotes = study.groups
        .filter((x) => x.formConfigIdForNotes)
        .map((x) => x.formConfigIdForNotes as number);

      const formResults = await getFormResults(study.id, undefined, undefined, patient.id, undefined, false);
      const data = getRecordResultListItemVms(
        study.patientFinishingFormId,
        formResults.data,
        formConfigIdsForNotes,
        patient.studyRouteProgress,
      );

      mandatoryForms = data.mandatoryForms;
      multipleForms = data.multipleForms;

      // provide certain Form result version for single export
      const filledRecords: FilledRecord[] = [
        {
          form: formConfig,
          stepName: stepName || '',
          resultVersions: [formResultVersion],
        },
      ];

      const recordsBlob = await createReport(
        <RecordsReportTemplate
          {...{
            mandatoryForms: mandatoryForms,
            multipleForms: multipleForms,
            filledRecords: filledRecords,
            missedRecords: [],
            studyNumber: study.studyNumber,
            studyName: study.title,
            patientUid: patient.uniqueId,
            profileFullName: profile.fullName,
            groups: study.groups,
            patientTags: patient.tags,
            patientId: patient.id,
            studyHasEcrf: study.hasEcrf,
          }}
        />,
      );

      triggerDownloadFileDialog(recordsBlob, normalizeFileName(fileName));
    },
    [profile.fullName, study],
  );
};
